import React from 'react';
import { Box, Typography, Switch, IconButton, Chip, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatDistanceToNow } from 'date-fns';
import { AIMood } from './types';
import { MOOD_PILL_COLOR } from './constants';
import { colors } from '../../../theme/designTokens';

interface MoodCardProps {
  mood: AIMood;
  position: number;
  inGrid: boolean;
  isMostPopular: boolean;
  onToggle: (id: number, currentActive: boolean) => void;
  onEdit: (mood: AIMood) => void;
  onDelete: (id: number) => void;
}

const MoodCard: React.FC<MoodCardProps> = ({
  mood,
  position,
  inGrid,
  isMostPopular,
  onToggle,
  onEdit,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: mood.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const keywords = Array.isArray(mood.emotional_keywords) ? mood.emotional_keywords : [];

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: inGrid ? '#fff' : '#f5f0e8',
        borderRadius: '10px',
        border: isOver
          ? '2px dashed #1A5F7A'
          : inGrid
            ? '1px solid #e2e8f0'
            : '1px solid #d6cfc2',
        borderLeft: inGrid ? `3px solid ${colors.deepTeal[500]}` : '3px solid #ccc',
        opacity: isDragging ? 0.28 : mood.is_active ? 1 : 0.48,
        transform: isDragging ? 'scale(0.96)' : undefined,
        boxShadow: isDragging
          ? '0 8px 24px rgba(0,0,0,0.15)'
          : isOver
            ? '0 0 0 2px rgba(26,95,122,0.2)'
            : '0 1px 3px rgba(0,0,0,0.06)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 150ms ease, opacity 150ms ease, border-color 150ms ease',
      }}
    >
      {/* Most Popular ribbon */}
      {isMostPopular && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bgcolor: colors.warmGold[500],
            color: '#fff',
            px: 1,
            py: 0.25,
            fontSize: '0.62rem',
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 700,
            borderRadius: '0 9px 0 6px',
            display: 'flex',
            alignItems: 'center',
            gap: 0.3,
            zIndex: 2,
          }}
        >
          <StarIcon sx={{ fontSize: 10 }} />
          Most Popular
        </Box>
      )}

      {/* Card body */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 1.5, gap: 1.2 }}>
        {/* Drag handle */}
        <Box
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            pt: 0.3,
            color: '#94a3b8',
            userSelect: 'none',
            '&:hover': { color: '#64748b' },
          }}
        >
          <Typography sx={{ fontSize: '1rem', lineHeight: 1, letterSpacing: '-0.05em' }}>&#x28FF;</Typography>
        </Box>

        {/* Position circle */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: inGrid ? colors.deepTeal[500] : '#d1d5db',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 700,
            fontSize: '0.72rem',
            flexShrink: 0,
            mt: 0.2,
          }}
        >
          {position}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
            {/* Color swatch */}
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: mood.color_primary,
                border: '1.5px solid rgba(0,0,0,0.1)',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 700,
                fontSize: '0.85rem',
                color: '#1e293b',
                lineHeight: 1.3,
              }}
            >
              {mood.display_name}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#64748b',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.4,
              mb: 0.6,
            }}
          >
            {mood.description}
          </Typography>

          {/* Emotional keywords */}
          {keywords.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mb: 0.8 }}>
              {keywords.slice(0, 3).map((kw, i) => (
                <Chip
                  key={i}
                  label={kw}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontFamily: '"Montserrat", sans-serif',
                    bgcolor: mood.color_secondary,
                    color: '#475569',
                    '& .MuiChip-label': { px: 0.6 },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Footer row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label="MOOD"
              sx={{
                height: 20,
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 700,
                fontSize: '0.6rem',
                letterSpacing: '0.04em',
                bgcolor: MOOD_PILL_COLOR.bg,
                color: MOOD_PILL_COLOR.color,
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
            <Tooltip title={mood.is_active ? 'Deactivate' : 'Activate'}>
              <Switch
                checked={mood.is_active}
                onChange={() => onToggle(mood.id, mood.is_active)}
                size="small"
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: colors.deepTeal[500] } }}
              />
            </Tooltip>
            <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontFamily: '"Montserrat", sans-serif' }}>
              {formatDistanceToNow(new Date(mood.updated_at), { addSuffix: true })}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <IconButton size="small" onClick={() => onEdit(mood)} sx={{ p: 0.3, color: colors.deepTeal[500] }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(mood.id)} sx={{ p: 0.3, color: '#ef4444' }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MoodCard;
